/**
 * EmbersShop — common-rarity cosmetics buyable with Embers.
 * Renders inside the CharacterProfileModal customize flow.
 *
 * Items the player already owns are dimmed + show "Owned." Buying
 * deducts embers atomically and list-appends the item to the right
 * inventory bucket on the server. Refresh stats on success so the
 * UI reflects the new wallet + inventory.
 */
import { useState } from 'react';
import { ShoppingBag, Flame as FlameIcon, Lock, X as XIcon } from 'lucide-react';
import { EMBERS_SHOP, type ShopItem } from '@tt/lobby/shop';
import { shopBuy, type CharacterStats } from '../../lib/api';
import { Character3DHero } from '../characters/Character3DHero';
import { Cosmetic3DThumbnail } from '../characters/CosmeticThumbnail3D';
import { IconAvatar } from '../characters/IconAvatar';
import { composeIconWithShopItem } from '../characters/cosmeticIcon';
import type { ThumbFrame } from '../characters/character3d-thumbnail';
import type { CharacterIcon } from '@tt/types';

/** Neutral base the Embers item is composed onto for its grid thumbnail — the
 *  same base CosmeticPreview / CosmeticThumbnail3D use, so the tile reads as
 *  "a plain character wearing this item" and the bake is shared across surfaces
 *  (cache-stable, independent of the player's equipped look). */
const EMBERS_THUMB_BASE: CharacterIcon = {
  set: 'default', id: 0, hue: 220, body: 0, hat: 0, face: 0, accentHue: 40,
} as unknown as CharacterIcon;

/** Pick the framing that best shows an Embers item's inventory bucket. Embers
 *  items are bodies / hats / faces / hues / accents / palettes — all head-region
 *  in 3D (the body-shape becomes the head silhouette), so all read head-close. */
function frameForBucket(_bucket: string): ThumbFrame {
  void _bucket;
  return 'portrait';
}

interface Props {
  charId: string;
  stats: CharacterStats | null;
  onPurchase: () => void; // tells parent to re-fetch stats
}

export function EmbersShop({ charId, stats, onPurchase }: Props) {
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);
  // Item the player tapped to preview. When it's a FIGURE cosmetic (hue /
  // accent / body / hat / face / palette) we show a live 3D preview of the
  // player wearing it; seat / emote / title items don't change the figure so
  // tapping them just clears the preview.
  const [previewItem, setPreviewItem] = useState<ShopItem | null>(null);
  const embers = stats?.embers ?? 0;

  const previewIcon = previewItem && stats?.icon
    ? composeIconWithShopItem(stats.icon, previewItem)
    : null;

  function ownsItem(it: ShopItem): boolean {
    if (!stats) return false;
    const inv = stats.inventory as unknown as Record<string, Array<string | number>>;
    const arr = inv[it.bucket];
    if (!Array.isArray(arr)) return false;
    return arr.includes(it.ref);
  }

  async function buy(it: ShopItem) {
    if (busy) return;
    setBusy(it.id);
    setMsg(null);
    try {
      await shopBuy(charId, it.id);
      setMsg({ kind: 'ok', text: `Bought ${it.label}!` });
      onPurchase();
    } catch (e) {
      const code = (e as { code?: string }).code;
      if (code === 'insufficient-embers') setMsg({ kind: 'err', text: 'Not enough Embers.' });
      else if (code === 'already-owned') setMsg({ kind: 'err', text: 'Already owned.' });
      else setMsg({ kind: 'err', text: 'Purchase failed.' });
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="profile-section embers-shop">
      <header className="embers-shop-head">
        <h3><ShoppingBag size={18} /> Embers shop</h3>
        <span className="embers-wallet">
          <FlameIcon size={14} /> {embers} Embers
        </span>
      </header>
      <p className="muted" style={{ margin: '0 0 0.6rem' }}>
        Common cosmetics for Embers. Earnable items still drop free through play —
        the shop is a shortcut. No real money.
      </p>
      {msg && (
        <div className={`embers-shop-msg ${msg.kind}`}>{msg.text}</div>
      )}
      {previewIcon && (
        <div className="embers-shop-preview" style={{ display: 'grid', placeItems: 'center', gap: 4, margin: '0 0 0.75rem' }}>
          <Character3DHero icon={previewIcon} size={160} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <small className="muted">Preview · {previewItem?.label}</small>
            <button
              type="button"
              className="btn ghost icon-btn"
              style={{ padding: '2px 8px' }}
              onClick={() => setPreviewItem(null)}
              aria-label="Close preview"
            >
              <XIcon size={12} />
            </button>
          </div>
        </div>
      )}
      <div className="embers-shop-grid">
        {EMBERS_SHOP.map((it) => {
          const owned = ownsItem(it);
          const tooPoor = embers < it.price;
          const previewable = stats?.icon ? composeIconWithShopItem(stats.icon, it) !== null : false;
          const isPreviewing = previewItem?.id === it.id;
          // 3D grid thumbnail: compose the item onto the neutral base so the
          // card shows the cosmetic as a baked 3D figure (falls back to the 2D
          // IconAvatar of that same composed icon while it renders / if WebGL is
          // off). Null ⇒ non-figure item (title / emote / seat) ⇒ text-only.
          const thumbIcon = composeIconWithShopItem(EMBERS_THUMB_BASE, it);
          return (
            <article
              key={it.id}
              className={`embers-shop-card rarity-${it.rarity}${owned ? ' owned' : ''}${tooPoor && !owned ? ' poor' : ''}${isPreviewing ? ' previewing' : ''}`}
              role={previewable ? 'button' : undefined}
              tabIndex={previewable ? 0 : undefined}
              style={previewable ? { cursor: 'pointer' } : undefined}
              title={previewable ? 'Tap to preview on your character' : undefined}
              onClick={previewable ? () => setPreviewItem(isPreviewing ? null : it) : undefined}
              onKeyDown={previewable ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPreviewItem(isPreviewing ? null : it); }
              } : undefined}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                {thumbIcon && (
                  <Cosmetic3DThumbnail
                    icon={thumbIcon}
                    frame={frameForBucket(it.bucket)}
                    size={44}
                    alt={it.label}
                    fallback={<IconAvatar icon={thumbIcon} size={44} alt={it.label} />}
                  />
                )}
                <div className="embers-shop-card-text" style={{ minWidth: 0 }}>
                  <strong>{it.label}</strong>
                  <small>{it.description}</small>
                </div>
              </div>
              <div className="embers-shop-card-action">
                {owned ? (
                  <span className="muted" style={{ fontSize: '0.78rem' }}>Owned</span>
                ) : (
                  <button
                    type="button"
                    className="btn ghost icon-btn embers-shop-buy"
                    onClick={(e) => { e.stopPropagation(); void buy(it); }}
                    disabled={busy === it.id || tooPoor}
                    title={tooPoor ? `Need ${it.price - embers} more Embers` : `Buy for ${it.price} Embers`}
                  >
                    {tooPoor ? <Lock size={12} /> : <FlameIcon size={12} />}
                    {busy === it.id ? '…' : `${it.price}`}
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
